import * as React from "react";
import type { ColumnSizingState } from "@tanstack/react-table";

const STORAGE_PREFIX = "table-v2:col-sizing:";
const getStorageKey = (tableKey: string) => `${STORAGE_PREFIX}${tableKey}`;

const readInitial = (tableKey: string | undefined): ColumnSizingState => {
  if (!tableKey || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(getStorageKey(tableKey));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as ColumnSizingState;
    }
    return {};
  } catch {
    return {};
  }
};

export const useColumnSizingPersistence = (
  tableKey: string | undefined,
  enabled: boolean,
) => {
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(
    () => (enabled ? readInitial(tableKey) : {}),
  );

  React.useEffect(() => {
    if (!enabled || !tableKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        getStorageKey(tableKey),
        JSON.stringify(columnSizing),
      );
    } catch {
      // quota / private mode — silently ignore
    }
  }, [columnSizing, enabled, tableKey]);

  return [columnSizing, setColumnSizing] as const;
};
